import React, { useEffect, useState } from 'react';
import { imageProxy } from 'tf-post-parser';
import Link from 'next/link';
import { getUser } from '../../helpers/token';
import RoleBadges from '../General/RoleBadges';
import { DisplayHeading, MUTED, PAPER_CARD } from '../Landing/ui';
import FutureImage from '../FutureImage';
import ProfileAvatar from './ProfileAvatar';
import AuthorProfileBar from './AuthorProfileBar';
import FollowButton from './FollowButton';
import BlogMenu from '../CuratorMenu/BlogMenu';

const AuthorProfileHeader = ({ data, profile }) => {
  const [bioExpanded, setBioExpanded] = useState(false);
  // Cookie check after mount only — reading it during render diverges from
  // the SSG'd HTML and breaks hydration on your own profile.
  const [isOwnProfile, setIsOwnProfile] = useState(false);

  useEffect(() => {
    setIsOwnProfile(getUser() === data.name);
  }, [data.name]);

  const hasSocial =
    profile &&
    (profile.website ||
      profile.twitter ||
      profile.facebook ||
      profile.instagram ||
      profile.youtube ||
      profile.pinterest ||
      profile.hive);

  const stats = profile
    ? [
        { label: 'Followers', value: profile.followers },
        { label: 'Following', value: profile.following },
        { label: 'Posts', value: profile.posts },
      ]
    : [];

  return (
    <div className="tw-bg-[#FAF8F1] dark:tw-bg-[#0E1A16]">
      {/* Cover strip — shorter fixed height so posts stay above the fold */}
      <div className="tw-relative tw-w-full tw-h-36 md:tw-h-44 tw-overflow-hidden tw-bg-[#EDE7D8] dark:tw-bg-[#1A2A24]">
        {data.coverImage && data.coverImage !== '' ? (
          <FutureImage
            src={imageProxy(data.coverImage, 1600)}
            alt=""
            fill
            priority
            sizes="100vw"
            className="tw-object-cover"
          />
        ) : null}
        <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-b tw-from-transparent tw-via-transparent tw-to-[rgba(14,26,22,0.35)]" />
      </div>

      {/* Profile row — horizontal on md+, centered stack on mobile */}
      <div className="tw-mx-auto tw-max-w-5xl tw-px-4 tw-pb-6">
        <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-items-start md:tw-gap-5">

          {/* Avatar — pops up over cover */}
          <div className="tw-flex tw-justify-center md:tw-justify-start tw--mt-12 md:tw--mt-14 tw-shrink-0">
            <div className="tw-rounded-full tw-ring-4 tw-ring-[#FAF8F1] dark:tw-ring-[#0E1A16]">
              <ProfileAvatar
                withBorder={false}
                authorAvatar={data.profileImage}
                author={data.name}
                size={100}
              />
            </div>
          </div>

          {/* Info column */}
          <div className="tw-flex-1 tw-min-w-0 tw-pt-3 tw-text-center md:tw-text-left">
            {/* Name + handle + badges */}
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center md:tw-justify-start tw-gap-2">
              <DisplayHeading as="h1" className="tw-text-xl sm:tw-text-2xl">
                {data.displayName && data.name !== data.displayName
                  ? data.displayName
                  : `@${data.name}`}
              </DisplayHeading>
              {data.displayName && data.name !== data.displayName && (
                <span className={`${MUTED} tw-text-sm`}>@{data.name}</span>
              )}
              {data.isBlacklisted && (
                <span className="tw-rounded-full tw-bg-red-100 dark:tw-bg-red-900/30 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-red-700 dark:tw-text-red-400">
                  Blacklisted
                </span>
              )}
              <RoleBadges
                roles={[
                  ...(data.isCurator ? ['team'] : []),
                  ...(data.isPaid ? ['paid'] : []),
                ]}
              />
            </div>

            {/* Bio */}
            {profile && profile.about && (
              <div className="tw-mt-1.5">
                <p
                  className={`${MUTED} tw-mb-0 tw-text-sm tw-leading-relaxed ${
                    !bioExpanded
                      ? 'tw-overflow-hidden [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box]'
                      : ''
                  }`}
                >
                  {profile.about}
                </p>
                {profile.about.length > 120 && (
                  <button
                    type="button"
                    onClick={() => setBioExpanded(e => !e)}
                    className="tw-border-0 tw-bg-transparent tw-cursor-pointer tw-text-xs tw-font-semibold tw-text-primary-700 dark:tw-text-primary-300 hover:tw-underline tw-p-0 tw-mt-0.5"
                  >
                    {bioExpanded ? 'Less' : 'More'}
                  </button>
                )}
              </div>
            )}

            {/* Stats + social in one compact row */}
            <div className="tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-justify-center md:tw-justify-start tw-gap-3">
              {stats.length > 0 && (
                <div className={`${PAPER_CARD} tw-inline-flex tw-rounded-xl tw-divide-x tw-divide-[#E7E0CF] dark:tw-divide-[#26382F]`}>
                  {stats.map(({ label, value }) => (
                    <div key={label} className="tw-px-3 tw-py-1.5 tw-text-center">
                      <p className="tw-m-0 tw-text-sm tw-font-bold tw-text-[#1D2D28] dark:tw-text-[#EBE6D9]">
                        {value ?? '—'}
                      </p>
                      <p className={`${MUTED} tw-m-0 tw-text-[11px]`}>{label}</p>
                    </div>
                  ))}
                </div>
              )}
              {hasSocial && (
                <div className="tw-shrink-0">
                  <AuthorProfileBar profile={profile} />
                </div>
              )}
            </div>
          </div>

          {/* Action button — right on desktop, centered on mobile */}
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center md:tw-justify-end tw-gap-2 tw-pt-3 md:tw-pt-4 tw-shrink-0">
            {isOwnProfile ? (
              <Link
                href="/dashboard/profile"
                className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-primary-600 tw-px-5 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-no-underline tw-shadow-[0_8px_20px_-8px_rgba(0,137,123,0.6)] tw-transition-all tw-duration-200 hover:-tw-translate-y-0.5 hover:tw-bg-primary-700 hover:tw-text-white"
              >
                Edit Profile
              </Link>
            ) : (
              <FollowButton author={data.name} btnstyle="pill" />
            )}
            <BlogMenu
              author={data.name}
              isCurator={data.isCurator}
              isVideo={data.isVideo}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

AuthorProfileHeader.defaultProps = {
  data: {
    name: '',
    isBlacklisted: false,
    isCurator: false,
    isPaid: false,
  },
};

export default AuthorProfileHeader;
